import { useState, useEffect } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { useSubscription } from "@/hooks/useSubscription";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Heart, Trash2, Sparkles, CreditCard, ExternalLink, Loader2, Download } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Favorite {
  id: string;
  calculator_path: string;
  calculator_name: string;
  created_at: string;
}

export default function Membership() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { role, hasAccess } = useUserRole();
  const { subscription, isLoading: isLoadingSubscription, getTierInfo, MEMBERSHIP_TIERS } = useSubscription();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [isLoadingFavorites, setIsLoadingFavorites] = useState(true);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('yearly');

  const isMember = hasAccess(["admin", "paid", "student"]);
  const isAdmin = hasAccess(["admin"]);
  const currentTier = getTierInfo();

  useEffect(() => {
    if (user) {
      fetchFavorites();
    }
  }, [user]);

  const fetchFavorites = async () => {
    if (!user) return;

    try {
      setIsLoadingFavorites(true);
      const { data, error } = await supabase
        .from("favorites")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      setFavorites(data || []);
    } catch (error) {
      console.error("Error fetching favorites:", error);
    } finally {
      setIsLoadingFavorites(false);
    }
  };

  const removeFavorite = async (favoriteId: string) => {
    try {
      const { error } = await supabase.from("favorites").delete().eq("id", favoriteId);

      if (error) throw error;

      setFavorites(favorites.filter((f) => f.id !== favoriteId));
      toast.success("Removed from favorites");
    } catch (error) {
      console.error("Error removing favorite:", error);
      toast.error("Failed to remove favorite");
    }
  };

  const handleCancelMembership = async () => {
    if (!user) return;

    try {
      setIsProcessing(true);

      // Remove paid/student role (keep admin if they have it)
      const { error } = await supabase
        .from("user_roles")
        .delete()
        .eq("user_id", user.id)
        .in("role", ["paid" as any, "student" as any]);

      if (error) throw error;

      toast.success("Membership cancelled successfully");
      setShowCancelDialog(false);

      // Refresh the page to update role state
      window.location.reload();
    } catch (error) {
      console.error("Error cancelling membership:", error);
      toast.error("Failed to cancel membership");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;

    try {
      setIsProcessing(true);

      // Call the secure delete-account edge function
      const { data, error } = await supabase.functions.invoke('delete-account', {
        method: 'POST',
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to delete account');
      }

      toast.success("Account deleted successfully");

      // Sign out and redirect
      await supabase.auth.signOut();
      navigate("/");
    } catch (error) {
      console.error("Error deleting account:", error);
      toast.error("Failed to delete account. Please try again or contact support.");
    } finally {
      setIsProcessing(false);
      setShowDeleteDialog(false);
    }
  };

  const handleCheckout = async (priceId: string) => {
    if (!user) {
      toast.error("Please sign in to continue");
      navigate("/auth");
      return;
    }

    // Check if student tier and email is not .edu
    const isStudentTier = priceId === MEMBERSHIP_TIERS.student.yearly.priceId || priceId === MEMBERSHIP_TIERS.student.monthly.priceId;
    if (isStudentTier && !user.email?.toLowerCase().endsWith(".edu")) {
      toast.error("Student & Educator membership requires a .edu email address");
      return;
    }

    try {
      setIsCheckoutLoading(true);
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId },
      });
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error: any) {
      console.error("Error creating checkout session:", error);
      toast.error(error?.message || "Failed to start checkout process");
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!user) return;
    try {
      setIsProcessing(true);
      const { data, error } = await supabase.functions.invoke("customer-portal");
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (error) {
      console.error("Error opening customer portal:", error);
      toast.error("Failed to open subscription management");
    } finally {
      setIsProcessing(false);
    }
  };

  // Render different content based on user role/subscription
  const renderContent = () => {
    // Paid members (student, paid, or admin)
    if (isMember) {
      return (
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Current Plan Status */}
          <Card>
            <CardHeader>
              <CardTitle>Your Membership</CardTitle>
              <CardDescription>Manage your account and subscription</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {isLoadingSubscription ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : subscription.subscribed && currentTier ? (
                <div className="p-4 bg-accent/10 border border-accent rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-accent" />
                        Current Plan: {currentTier.name}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {currentTier.price} • Renews on {new Date(subscription.subscriptionEnd!).toLocaleDateString()}
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={handleManageSubscription} disabled={isProcessing}>
                      <CreditCard className="h-4 w-4 mr-2" />
                      Manage
                    </Button>
                  </div>
                </div>
              ) : null}

              <div className="flex flex-wrap gap-2">
                <Button onClick={() => navigate("/supercalc")}>Open SuperCalc</Button>
                <Button variant="secondary" onClick={() => navigate("/install")} className="gap-2">
                  <Download className="h-4 w-4" />
                  Install App
                </Button>
                {isAdmin && (
                  <Button variant="outline" onClick={() => navigate("/admin")}>
                    Admin Panel
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Favorites */}
          {favorites.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="h-5 w-5 text-accent fill-accent" />
                  Your Favorites
                </CardTitle>
                <CardDescription>Quick access to your favorite calculators</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2">
                  {favorites.map((favorite) => (
                    <Card key={favorite.id} className="p-3">
                      <div className="flex items-center justify-between">
                        <Link
                          to={favorite.calculator_path}
                          className="flex-1 text-sm font-medium hover:text-primary transition-colors"
                        >
                          {favorite.calculator_name}
                        </Link>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFavorite(favorite.id)}
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Account Management */}
          <Card>
            <CardHeader>
              <CardTitle>Account Management</CardTitle>
              <CardDescription>Manage your account settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <h3 className="font-medium">Signed in as</h3>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
                <Button variant="outline" onClick={() => supabase.auth.signOut()}>
                  Sign Out
                </Button>
              </div>

              {!isAdmin && (
                <div className="space-y-2 pt-4 border-t">
                  <h3 className="font-medium">Cancel Membership</h3>
                  <p className="text-sm text-muted-foreground">
                    Cancel your membership and lose access to SuperCalc. You can re-subscribe anytime.
                  </p>
                  <Button variant="outline" onClick={() => setShowCancelDialog(true)}>
                    Cancel Membership
                  </Button>
                </div>
              )}

              <div className="pt-4 border-t space-y-2">
                <h3 className="font-medium text-destructive">Danger Zone</h3>
                <p className="text-sm text-muted-foreground">
                  Permanently delete your account and all associated data. This action cannot be undone.
                </p>
                <Button variant="destructive" onClick={() => setShowDeleteDialog(true)}>
                  Delete Account
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Free users - show pricing and subscription options
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Membership & Pricing</CardTitle>
            <CardDescription>Try SuperCalc free, then upgrade for unlimited access</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Free Trial Info */}
            <div className="p-4 bg-muted/50 rounded-lg">
              <h3 className="font-semibold mb-2 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-accent" />
                Free Trial Included
              </h3>
              <p className="text-sm text-muted-foreground">
                Every account gets <strong>1 free SuperCalc query</strong> to test the AI solver before upgrading. No
                credit card required to try it out!
              </p>
            </div>

            {/* Pricing Transparency */}
            <div className="space-y-3">
              <h3 className="font-semibold">Why SuperCalc Costs Money</h3>
              <p className="text-sm text-muted-foreground">
                SuperCalc uses advanced AI models (GPT-5, Gemini) that charge us for every query. Our pricing is
                designed to cover these operational costs and keep the service sustainable—we're not trying to maximize
                profits. Your subscription helps us provide accurate, reliable mathematical solutions powered by
                cutting-edge AI.
              </p>
            </div>

            {/* Billing Period Toggle */}
            <div className="flex items-center justify-center gap-4 p-4 bg-muted/30 rounded-lg">
              <Label htmlFor="billing-toggle" className={billingPeriod === 'yearly' ? 'font-semibold' : 'text-muted-foreground'}>
                Yearly <span className="text-accent text-xs ml-1">(Save 17%)</span>
              </Label>
              <Switch
                id="billing-toggle"
                checked={billingPeriod === 'monthly'}
                onCheckedChange={(checked) => setBillingPeriod(checked ? 'monthly' : 'yearly')}
              />
              <Label htmlFor="billing-toggle" className={billingPeriod === 'monthly' ? 'font-semibold' : 'text-muted-foreground'}>
                Monthly
              </Label>
            </div>

            {/* Membership Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="border-2">
                <CardHeader>
                  <CardTitle className="text-lg">Student & Educator</CardTitle>
                  <CardDescription>Perfect for learners & teachers</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-3xl font-bold">
                    ${billingPeriod === 'yearly' ? '44' : '4.40'}
                    <span className="text-sm font-normal text-muted-foreground">/{billingPeriod === 'yearly' ? 'year' : 'month'}</span>
                  </p>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    <li>✓ Unlimited SuperCalc access</li>
                    <li>✓ AI lesson plan generator</li>
                    <li>✓ All calculators & tools</li>
                    <li>✓ Favorites feature</li>
                    <li>✓ Priority support</li>
                  </ul>
                  <p className="text-xs font-medium text-accent pt-2">Requires .edu email address</p>
                  <p className="text-xs text-muted-foreground pt-2 border-t">
                    7-day refund available (minus usage costs)
                  </p>
                  {user && (
                    <Button
                      className="w-full mt-4"
                      onClick={() => handleCheckout(MEMBERSHIP_TIERS.student[billingPeriod].priceId)}
                      disabled={isCheckoutLoading}
                    >
                      {isCheckoutLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <ExternalLink className="h-4 w-4 mr-2" />
                      )}
                      Subscribe
                    </Button>
                  )}
                </CardContent>
              </Card>

              <Card className="border-2 border-accent/50">
                <CardHeader>
                  <CardTitle className="text-lg">Paid Member</CardTitle>
                  <CardDescription>Full access + priority support</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-3xl font-bold">
                    ${billingPeriod === 'yearly' ? '88' : '8.80'}
                    <span className="text-sm font-normal text-muted-foreground">/{billingPeriod === 'yearly' ? 'year' : 'month'}</span>
                  </p>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    <li>✓ Everything in Student & Educator</li>
                    <li>✓ Enhanced priority support</li>
                    <li>✓ Early access to features</li>
                    <li>✓ Support CalcLab development</li>
                  </ul>
                  <p className="text-xs text-muted-foreground pt-2 border-t">
                    7-day refund available (minus usage costs)
                  </p>
                  {user && (
                    <Button
                      className="w-full mt-4"
                      onClick={() => handleCheckout(MEMBERSHIP_TIERS.paid[billingPeriod].priceId)}
                      disabled={isCheckoutLoading}
                    >
                      {isCheckoutLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <ExternalLink className="h-4 w-4 mr-2" />
                      )}
                      Subscribe
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Refund Policy */}
            <div className="p-4 bg-muted/50 rounded-lg text-sm space-y-2">
              <h4 className="font-semibold">Refund Policy</h4>
              <p className="text-muted-foreground">
                We offer a 7-day money-back guarantee. If you're not satisfied, we'll refund your subscription minus the
                actual cost of any SuperCalc queries you used (based on AI model token costs). See our{" "}
                <Link to="/terms" className="text-accent hover:underline">
                  Terms & Conditions
                </Link>{" "}
                for details.
              </p>
            </div>

            {!user && (
              <>
                <p className="text-muted-foreground">
                  Sign in or create an account to get started with your free trial.
                </p>
                <Button onClick={() => navigate("/auth")}>Sign In / Sign Up</Button>
              </>
            )}

            {user && (
              <p className="text-muted-foreground">
                You are signed in as <span className="font-medium">{user.email}</span>. Choose a membership tier above to unlock unlimited SuperCalc access.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Show favorites for free users if they have any */}
        {user && favorites.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Heart className="h-5 w-5 text-accent fill-accent" />
                Your Favorites
              </CardTitle>
              <CardDescription>Quick access to your favorite calculators</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-2">
                {favorites.map((favorite) => (
                  <Card key={favorite.id} className="p-3">
                    <div className="flex items-center justify-between">
                      <Link
                        to={favorite.calculator_path}
                        className="flex-1 text-sm font-medium hover:text-primary transition-colors"
                      >
                        {favorite.calculator_name}
                      </Link>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFavorite(favorite.id)}
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Account Management for free users */}
        {user && (
          <Card>
            <CardHeader>
              <CardTitle>Account Management</CardTitle>
              <CardDescription>Manage your account settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <h3 className="font-medium">Signed in as</h3>
                <p className="text-sm text-muted-foreground">{user.email}</p>
                <Button variant="outline" onClick={() => supabase.auth.signOut()}>
                  Sign Out
                </Button>
              </div>

              <div className="pt-4 border-t space-y-2">
                <h3 className="font-medium text-destructive">Danger Zone</h3>
                <p className="text-sm text-muted-foreground">
                  Permanently delete your account and all associated data. This action cannot be undone.
                </p>
                <Button variant="destructive" onClick={() => setShowDeleteDialog(true)}>
                  Delete Account
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  return (
    <PageLayout>
      {renderContent()}

      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Membership?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove your member access and you will no longer be able to use SuperCalc. You can re-subscribe
              anytime from the membership page.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isProcessing}>Keep Membership</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancelMembership} disabled={isProcessing}>
              {isProcessing ? "Cancelling..." : "Cancel Membership"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Account Permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete your account and remove all your data from our
              servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isProcessing}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAccount}
              disabled={isProcessing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isProcessing ? "Deleting..." : "Delete Account"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  );
}
